import { useRef, useState, useEffect, useCallback, useContext } from 'react'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import { createUseStyles } from 'react-jss'
import { Context as GeneralContext } from '@/context'
import { Context as LoadingContext } from '@/context/loading'
import usePrevious from '@/hooks/usePrevious'
import useUpdateEffect from '@/hooks/useUpdateEffect'
import LocomotiveScroll from '@/components/LocomotiveScroll'
import Form from '@/components/Form'
import Footer from '@/components/Footer'
import Meta from '@/components/Meta'
import PageTitle from '@/components/PageTitle'
import actions from '@/actions'
import { externalPreload } from '@/utils/dom'
import utils from '@/utils'
import style from './style'

const useStyles = createUseStyles(style)

const Contact = ({
  stub,
}) => {
  const { headerHeight } = useContext(GeneralContext)
  const { setReady, ready } = useContext(LoadingContext)
  const classes = useStyles({ headerHeight })
  const data = useRef([])
  const [load, setLoad] = useState(false)
  const [feedback, setFeedback] = useState({})

  /*------------------------------
  Redux Connect
  ------------------------------*/
  const { current, page, mediaQueryKey, form, response, strings } = useSelector((state) => ({
    current: state.page.current,
    page: state.page.pages[stub],
    mediaQueryKey: state.mediaQuery.key,
    form: state.page.forms,
    response: state.page.status_form,
    strings: state.language.string,
  }), shallowEqual)

  /*------------------------------
  Redux Actions
  ------------------------------*/
  const dispatch = useDispatch()
  const getPage = useCallback((index) => dispatch(actions.page.getDataPage(index)), [dispatch])
  const setCurrentPage = useCallback((index) => dispatch(actions.page.setCurrent(index)), [dispatch])
  const getForm = useCallback((index) => dispatch(actions.page.getDataForm(index)), [dispatch])
  const sendForm = useCallback((index, params) => dispatch(actions.page.sendForm(index, params)), [dispatch])
  const resetForm = useCallback(() => dispatch(actions.page.resetForm()), [dispatch])
  const sendDataToCallback = useCallback((value) => dispatch(actions.page.sendDataToCallback(value)), [dispatch])

  /*------------------------------
  Complete Preload
  ------------------------------*/
  const completePreload = () => {
    externalPreload(setLoad)
    setReady(true)
  }

  /*------------------------------
  Get Data
  ------------------------------*/
  const getData = useCallback(() => {
    if (page !== undefined) {
      data.current = { ...page }
      getForm(data.current.contact_form_id)
    }
  }, [ready, page])

  /*------------------------------
  Have Form
  ------------------------------*/
  const haveForm = () => {
    const { contact_form_id } = data.current
    if (form !== undefined && contact_form_id !== undefined) {
      completePreload()
    }
  }

  /*------------------------------
  Did Update
  ------------------------------*/
  const prevForm = usePrevious(form)
  const prevMediaQueryKey = usePrevious(mediaQueryKey)
  const prevContent = usePrevious(page)

  useUpdateEffect(() => {
    if (prevContent !== page) getData()
    if (prevMediaQueryKey !== mediaQueryKey) getData()
    if (prevForm !== form) haveForm()
  }, [page, mediaQueryKey, form])

  /*------------------------------
  Initialize
  ------------------------------*/
  const initialize = () => {
    if (current !== stub) {
      setCurrentPage(stub, 'pages')
    }
    getPage(stub)
    getData()
  }

  /*------------------------------
  Did Mount
  ------------------------------*/
  useEffect(() => {
    if (!load) initialize()
  }, [])

  /*------------------------------
  Render Helmet
  ------------------------------*/
  const renderHelmet = () => {
    return load && (
      <Meta
        title={page.yoast_meta.find((y) => y.property?.includes('title')).content || page.title.rendered}
        meta={page.yoast_meta}
        schema={page.yoast_json_ld}
      />
    )
  }

  /*------------------------------
  Render Cover
  ------------------------------*/
  const renderCover = () => {
    return (
      <div className={classes.cover}>
        <PageTitle
          title={page.contact_form_title}
          delay={1}
        />
      </div>
    )
  }

  /*------------------------------
  Callback Form
  ------------------------------*/
  const callbackForm = (value) => {
    sendForm(data.current.contact_form_id, value)
  }

  /*------------------------------
  Risposta form
  ------------------------------*/
  useUpdateEffect(() => {
    setFeedback(response)
  }, [response])

  /*------------------------------
  Render Form
  ------------------------------*/
  const renderForm = () => {
    const { contact_form_id, contact_form_text, contact_form_btn } = data.current
    const contactForm = form[contact_form_id]

    return (
      <>
        <div className={classes.wrap}>
          <div className={classes.formContent}>
            <div
              className={classes.formContentText}
              dangerouslySetInnerHTML={{ __html: contact_form_text }}
            />
          </div>
        </div>
        <div className={`${classes.wrap} ${classes.wrapPink}`}>
          <div className={`${classes.formContent} ${classes.formContentCenter}`}>
            <Form
              data={contactForm.data}
              newsletter={strings.acceptance_newsletter}
              privacy={strings.acceptance_privacy_text}
              privacyLink={utils.path.cleanOrigin(strings.acceptance_privacy_link)}
              labelSubmit={contact_form_btn}
              resetForm={resetForm}
              sendForm={callbackForm}
              parent={this}
              response={feedback}
              mailchimpCallback={(value) => sendDataToCallback(value)}
            />
          </div>
        </div>
      </>
    )
  }

  /*------------------------------
  Render Footer
  ------------------------------*/
  const renderFooter = () => {
    return <Footer newsletter={false} noMargin />
  }

  /*------------------------------
  Render Content
  ------------------------------*/
  const renderContent = () => {
    return load && (
      <>
        {renderCover()}
        {renderForm()}
        {renderFooter()}
      </>
    )
  }

  return (
    <LocomotiveScroll
      init={load}
      className={`page pageContact ${classes.root}`}
    >
      {renderHelmet()}
      {renderContent()}
    </LocomotiveScroll>
  )
}

export default Contact
